import React, { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import { Switch } from '../../ui/switch'
import { Badge } from '../../ui/badge'
import { ColorPicker } from '../../ui/color-picker'
import { CreditCard, Loader2, Palette, Upload, X } from 'lucide-react'
import { ClientRequest } from '../../../requests/ClientRequest'
import { BadgeConfigurationRequest } from '../../../requests/BadgeConfigurationRequest'
import ClientEndpoints from '../../../endpoints/ClientEndpoints'
import BadgeTemplateEndpoints, { BadgeTemplate } from '../../../endpoints/BadgeTemplateEndpoints'
import { BadgePropertyOverrides } from '../../common/BadgePropertyOverrides'
import { BadgePrintingLogic } from '../../../common/BadgePrintingLogic'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'

interface ClientBadgeSectionProps {
  formData: ClientRequest
  onChange: (field: keyof ClientRequest, value: any) => void
  onBadgeConfigChange: (field: keyof BadgeConfigurationRequest, value: any) => void
  onSave: () => void
  isSaving: boolean
  isAdmin: boolean
  clientId?: number
}

export const ClientBadgeSection: React.FC<ClientBadgeSectionProps> = ({
  formData,
  onChange,
  onBadgeConfigChange,
  onSave,
  isSaving,
  isAdmin,
  clientId,
}) => {
  const [templates, setTemplates] = useState<BadgeTemplate[]>([])

  useEffect(() => {
    BadgeTemplateEndpoints.getTemplates(true, undefined, clientId)
      // A client that hasn't been created yet can only use global templates
      .then((data) => (clientId == null ? data.filter((t) => t.isGlobal) : data))
      .then((data) => setTemplates(data.length > 0 ? data : [
        { id: 1, name: 'Vertical (Default)', isSystem: true, isActive: true, isLayoutBased: false, isGlobal: true, clientIds: [], category: 'DEFAULT', orientation: 'PORTRAIT', size: 'STANDARD', width: 210, height: 325, createdBy: 'System', createdAt: '', updatedAt: '' },
        { id: 2, name: 'Horizontal', isSystem: true, isActive: true, isLayoutBased: false, isGlobal: true, clientIds: [], category: 'DEFAULT', orientation: 'LANDSCAPE', size: 'STANDARD', width: 325, height: 210, createdBy: 'System', createdAt: '', updatedAt: '' },
      ]))
      .catch(() => setTemplates([
        { id: 1, name: 'Vertical (Default)', isSystem: true, isActive: true, isLayoutBased: false, isGlobal: true, clientIds: [], category: 'DEFAULT', orientation: 'PORTRAIT', size: 'STANDARD', width: 210, height: 325, createdBy: 'System', createdAt: '', updatedAt: '' },
        { id: 2, name: 'Horizontal', isSystem: true, isActive: true, isLayoutBased: false, isGlobal: true, clientIds: [], category: 'DEFAULT', orientation: 'LANDSCAPE', size: 'STANDARD', width: 325, height: 210, createdBy: 'System', createdAt: '', updatedAt: '' },
      ]))
  }, [clientId])

  const selectedTemplate = useMemo(() => {
    const tid = formData.badgeConfiguration?.templateId || 1
    return templates.find((t) => t.id === tid) || null
  }, [templates, formData.badgeConfiguration?.templateId])

  const propertyOverrides: Record<string, string> = formData.badgeConfiguration?.propertyOverrides || {}

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Badge Configuration
        </CardTitle>
        <CardDescription>Configure client-level badge appearance</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-3">
          <Switch
            checked={formData.clientBadgesEnabled}
            onCheckedChange={(checked) => onChange('clientBadgesEnabled', checked)}
            disabled={!isAdmin || isSaving}
          />
          <Label>Enable client-level badges</Label>
        </div>

        {formData.clientBadgesEnabled && (
          <div className="space-y-6 pt-2">
            {/* Template selector */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Badge Name</Label>
                <Input
                  value={formData.badgeConfiguration?.badgeName || ''}
                  onChange={(e) => onBadgeConfigChange('badgeName', e.target.value)}
                  disabled={!isAdmin || isSaving}
                  placeholder="Badge name"
                />
              </div>

              <div className="space-y-2">
                <Label>Badge Template</Label>
                <Select
                  value={(formData.badgeConfiguration?.templateId || 1).toString()}
                  onValueChange={(value) => onBadgeConfigChange('templateId', parseInt(value) || 1)}
                  disabled={!isAdmin || isSaving}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id.toString()}>
                        <span className="flex items-center gap-2">
                          {t.name}
                          {t.isLayoutBased && (
                            <Badge variant="secondary" className="text-[10px] px-1 py-0">Visual</Badge>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTemplate && (
                  <p className="text-xs text-muted-foreground">
                    {selectedTemplate.orientation} &middot; {selectedTemplate.width}×{selectedTemplate.height}mm
                    {selectedTemplate.isLayoutBased && ' · Visual builder template'}
                  </p>
                )}
              </div>
            </div>

            {/* Colors - only shown for SVG-based templates */}
            {selectedTemplate && !selectedTemplate.isLayoutBased ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Palette className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Colors</Label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <ColorPicker
                    label="Primary Color"
                    color={formData.badgeConfiguration?.primaryColour || '#1c5ca2'}
                    onChange={(color) => onBadgeConfigChange('primaryColour', color)}
                    disabled={!isAdmin || isSaving}
                  />

                  <ColorPicker
                    label="Secondary Color"
                    color={formData.badgeConfiguration?.secondaryColour || '#304c75'}
                    onChange={(color) => onBadgeConfigChange('secondaryColour', color)}
                    disabled={!isAdmin || isSaving}
                  />

                  <ColorPicker
                    label="Role Text Color"
                    color={formData.badgeConfiguration?.roleTextColour || '#ffffff'}
                    onChange={(color) => onBadgeConfigChange('roleTextColour', color)}
                    disabled={!isAdmin || isSaving}
                  />
                </div>
              </div>
            ) : selectedTemplate?.isLayoutBased ? (
              <BadgePropertyOverrides
                template={selectedTemplate}
                overrides={propertyOverrides}
                onChange={(next) => {
                  onBadgeConfigChange('propertyOverrides',
                    Object.keys(next).length > 0 ? next : null)
                }}
                disabled={!isAdmin || isSaving}
              />
            ) : null}

            {/* Images */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Logo Image</Label>
                <div className="relative border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 hover:border-muted-foreground/40 cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={!isAdmin || isSaving}
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      const reader = new FileReader()
                      reader.onload = () => {
                        const base64 = (reader.result as string).split(',')[1]
                        onBadgeConfigChange('base64LogoImage', base64)
                        onBadgeConfigChange('logoImageContentType', file.type)
                      }
                      reader.readAsDataURL(file)
                    }}
                  />
                  {formData.badgeConfiguration?.base64LogoImage ? (
                    <>
                      <img
                        src={`data:${formData.badgeConfiguration.logoImageContentType};base64,${formData.badgeConfiguration.base64LogoImage}`}
                        alt="Logo"
                        className="max-w-full max-h-24 mx-auto rounded"
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2 h-6 w-6 p-0"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          onBadgeConfigChange('base64LogoImage', null)
                          onBadgeConfigChange('logoImageContentType', null)
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </>
                  ) : (
                    <div className="text-center">
                      <Upload className="mx-auto h-6 w-6 text-muted-foreground mb-1" />
                      <p className="text-xs text-muted-foreground">Click to upload a logo image</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Default badge photo: stands in for people with no photo of their own */}
              <div className="space-y-2">
                <Label>Default Badge Photo</Label>
                <div className="relative border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 hover:border-muted-foreground/40 cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={!isAdmin || isSaving}
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      const reader = new FileReader()
                      reader.onload = () => {
                        const base64 = (reader.result as string).split(',')[1]
                        onBadgeConfigChange('base64DefaultPictureImage', base64)
                        onBadgeConfigChange('defaultPictureImageContentType', file.type)
                      }
                      reader.readAsDataURL(file)
                    }}
                  />
                  {formData.badgeConfiguration?.base64DefaultPictureImage ? (
                    <>
                      <img
                        src={`data:${formData.badgeConfiguration.defaultPictureImageContentType};base64,${formData.badgeConfiguration.base64DefaultPictureImage}`}
                        alt="Default badge photo"
                        className="max-w-full max-h-24 mx-auto rounded"
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2 h-6 w-6 p-0"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          onBadgeConfigChange('base64DefaultPictureImage', null)
                          onBadgeConfigChange('defaultPictureImageContentType', null)
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </>
                  ) : (
                    <div className="text-center">
                      <Upload className="mx-auto h-6 w-6 text-muted-foreground mb-1" />
                      <p className="text-xs text-muted-foreground">Click to upload a default photo</p>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Used on new badges for anyone who doesn't have a photo yet. Leave empty
                  to fall back to the generic silhouette.
                </p>
              </div>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="border-t pt-4 flex justify-end gap-2">
            {formData.clientBadgesEnabled && clientId && (
              <Button
                variant="outline"
                onClick={() => {
                  if (selectedTemplate?.isLayoutBased) {
                    BadgePrintingLogic(() => BadgeTemplateEndpoints.previewTemplate(
                      selectedTemplate.id,
                      propertyOverrides,
                      formData.badgeConfiguration?.base64LogoImage,
                      formData.badgeConfiguration?.logoImageContentType,
                      null,
                      formData.badgeConfiguration?.base64DefaultPictureImage,
                      formData.badgeConfiguration?.defaultPictureImageContentType,
                    ))
                      .catch((error) => toast.error(extractErrorMessage(error, 'Failed to generate badge preview')))
                  } else {
                    BadgePrintingLogic(() => ClientEndpoints.getBadgePreview(clientId!))
                      .catch((error) => toast.error(extractErrorMessage(error, 'Failed to generate badge preview')))
                  }
                }}
                disabled={isSaving}
              >
                Preview Badge
              </Button>
            )}
            <Button onClick={onSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Configuration'
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
